<!-- z-1 lifts the hero's stacking context above the workspace that follows it,
     so popovers anchored in the hero are not painted under it. -->
<section
  class="hero relative isolate z-1 mx-4 mt-4 rounded-[18px] border-2 border-white text-[#1b1c19] antialiased"
  [class.hero--loading]="loading"
>
  <div class="hero__bg" aria-hidden="true"></div>

  <div
    class="flex flex-col gap-3.5 px-5.5 pt-4.5 pb-4 max-sm:px-4 max-sm:pt-3.5"
  >
    <!-- The title keeps at least 18rem; when the pills and actions no longer
         fit beside it they wrap to a second line rather than squeezing the
         title to a few letters. -->
    <div class="flex flex-wrap items-center gap-3">
      <h1
        class="hero__title min-w-72 flex-auto truncate text-[26px] leading-tight font-bold tracking-[-0.015em] max-sm:text-[22px]"
        tabindex="-1"
        [attr.title]="entry()?.title ?? pkg()?.name ?? null"
        data-testid="hero-price-title"
      >
        {{ entry()?.title ?? pkg()?.name ?? "Loading…" }}
      </h1>
      @if (priceStatus(); as status) {
      <span
        class="inline-flex h-7 shrink-0 items-center gap-2 rounded-full border px-3 text-[12.5px] font-semibold whitespace-nowrap"
        [class]="priceToneClass[status.tone].pill"
        [attr.data-tone]="status.tone"
        [enrTooltip]="status.next ? status.meaning + ' ' + status.next : status.meaning"
        enrTooltipPosition="bottom"
        [enrTooltipShowDelay]="0"
        data-testid="hero-price-status"
      >
        <span
          class="size-2 shrink-0 rounded-full"
          [class]="priceToneClass[status.tone].dot"
          aria-hidden="true"
        ></span>
        <span
          >{{ status.label }}@if (status.suffix) {<span class="font-medium">
            · {{ status.suffix }}</span
          >}</span
        >
      </span>
      } @if (channelLabel(); as channel) {
      <!-- The nav header still reads "All channels" for an admin, so the
           channel this page writes to has to be said here, at the same
           weight as the status it qualifies. -->
      <span
        class="inline-flex h-7 shrink-0 items-center gap-1.5 rounded-full border border-white bg-white/70 px-3 text-[12.5px] font-semibold whitespace-nowrap text-[#2a2a2a]"
        [enrTooltip]="
          'This page edits the ' +
          channel +
          ' enrichment of this ' +
          (entry() ? 'price' : 'package') +
          '. Other channels keep their own.'
        "
        enrTooltipPosition="bottom"
        [enrTooltipShowDelay]="0"
        data-testid="hero-channel"
      >
        <span class="hero__channel-icon material-icons" aria-hidden="true"
          >storefront</span
        >
        <span class="font-medium text-[#555]">Editing on</span>
        {{ channel }}
      </span>
      }

      <div class="hero__actions ml-auto flex flex-none items-center gap-1.5">
        @if (saveStateLabel(); as label) {
        <span
          class="mr-1 inline-flex items-center gap-1.5 text-xs whitespace-nowrap text-[#555]"
          [attr.aria-live]="saveState === 'saving' ? 'polite' : 'off'"
        >
          @if (saveState === 'saving') {
          <span class="hero__save-spinner" aria-hidden="true"></span>
          } @else if (saveState === 'saved') {
          <svg
            class="size-2.75 text-[#007a52]"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
          } @else if (saveState === 'dirty') {
          <span
            class="size-1.5 rounded-full bg-[#d6991a]"
            aria-hidden="true"
          ></span>
          } {{ label }}
        </span>
        }

        <!-- A native button rather than enr-button so it can carry
             `aria-pressed`: this is a two-state toggle, not an action. -->
        <button
          type="button"
          class="inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-lg border border-white bg-white/50 px-2.5 text-[13px] font-semibold text-[#2a2a2a] transition-colors hover:bg-white/75 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[#1dc286] aria-pressed:border-[#00c07a] aria-pressed:bg-white/75 aria-pressed:text-[#007a52]"
          [attr.aria-pressed]="previewOpen"
          [title]="previewOpen ? 'Hide preview' : 'Show preview'"
          (click)="togglePreview.emit()"
        >
          <svg
            class="size-3.75 shrink-0"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7Z" />
            <circle cx="12" cy="12" r="3" />
            @if (!previewOpen) {
            <path d="M3 3l18 18" />
            }
          </svg>
          <span>Preview</span>
        </button>
        <enr-button
          variant="text"
          size="medium"
          [disabled]="loading"
          (clicked)="cancelClick.emit()"
          >Cancel</enr-button
        >
        <!-- Publish is DISABLED, not hidden, while the price has blockers: the
             caret beside it gates on lifecycle, not readiness, and a lone
             caret with no label is unreadable. Missing PERMISSION is the one
             case that hides it, since `enrichment:publish` is all-or-nothing
             per user. Reject then stands on its own. -->
        @if (mayPublish) {
        <div
          class="hero__publish-split"
          [class.hero__publish-split--has-menu]="canReject"
        >
          <!-- The tooltip belongs to the Publish half only, so a Publish
               blocker is never explained over the caret, to someone reaching
               for Reject. -->
          <span
            class="hero__publish-primary"
            [enrTooltip]="publishHint"
            enrTooltipPosition="bottom"
            [enrTooltipShowDelay]="0"
          >
            <enr-button
              variant="primary"
              size="medium"
              [disabled]="loading || saveState === 'dirty' || !canPublish"
              (clicked)="publishClick.emit()"
              >Publish this price</enr-button
            >
          </span>

          <!-- Only rendered when the price is on sale, so the caret never
               opens onto a menu whose single item is dead. -->
          @if (canReject) {
          <button
            type="button"
            class="hero__publish-caret"
            cdkOverlayOrigin
            #publishMenuTrigger="cdkOverlayOrigin"
            [disabled]="loading"
            [attr.aria-expanded]="publishMenuOpen()"
            aria-haspopup="menu"
            aria-label="More publish actions"
            (click)="togglePublishMenu()"
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="3"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
          <ng-template
            cdkConnectedOverlay
            [cdkConnectedOverlayOrigin]="publishMenuTrigger"
            [cdkConnectedOverlayOpen]="publishMenuOpen()"
            [cdkConnectedOverlayPositions]="publishMenuPositions"
            [cdkConnectedOverlayPush]="true"
            [cdkConnectedOverlayHasBackdrop]="true"
            cdkConnectedOverlayBackdropClass="cdk-overlay-transparent-backdrop"
            (backdropClick)="closePublishMenu()"
          >
            <div class="hero__publish-menu" role="menu">
              <button
                type="button"
                role="menuitem"
                class="hero__publish-menu-item"
                (click)="onRejectClick()"
              >
                <mat-icon aria-hidden="true">block</mat-icon>
                <span class="hero__publish-menu-text">
                  <span class="hero__publish-menu-label"
                    >Reject (stop selling)</span
                  >
                  <span class="hero__publish-menu-hint">
                    Withdraw this price from the selected channel. Your
                    enrichment is kept.
                  </span>
                </span>
              </button>
            </div>
          </ng-template>
          }
        </div>
        } @else if (canReject) {
        <enr-button
          variant="error"
          size="medium"
          [disabled]="loading"
          (clicked)="onRejectClick()"
          [attr.data-testid]="'enrichment-hero-btn-reject'"
          >Reject</enr-button
        >
        }
      </div>
    </div>

    <div
      class="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-5 max-[900px]:grid-cols-1"
    >
      <div class="flex min-w-0 flex-col gap-2.25">
        @if (entry(); as e) {
        <div
          class="flex flex-wrap items-center gap-x-2.5 gap-y-2 text-[12.5px] text-[#555]"
          data-testid="hero-price-meta"
        >
          <ng-container *ngTemplateOutlet="eyebrow" />
          <span
            class="inline-flex h-6 items-center gap-1.5 rounded-full border border-white bg-white/70 px-2.25 text-[11.5px]"
          >
            POP
            <code class="font-mono text-[11px] text-[#007a52]">{{ e.id }}</code>
            <ng-container
              *ngTemplateOutlet="
                copyButton;
                context: {
                  $implicit: e.id,
                  label: 'Copy price ID ',
                  testId: 'hero-copy-price-id'
                }
              "
            />
          </span>
          @if (e.productCode) {
          <span class="text-[#c2c6bd]" aria-hidden="true">·</span>
          <span>Code <b class="text-[#1b1c19]">{{ e.productCode }}</b></span>
          } @if (figuresText(); as figures) {
          <span class="text-[#c2c6bd]" aria-hidden="true">·</span>
          <span data-testid="hero-price-figures">{{ figures }}</span>
          }
        </div>
        } @else {
        <div
          class="flex flex-wrap items-center gap-x-2.5 gap-y-2 text-[12.5px] text-[#555]"
        >
          <ng-container *ngTemplateOutlet="eyebrow" />
          @if (pkg(); as p) {
          <span
            class="inline-flex h-6 items-center gap-1.5 rounded-full border border-white bg-white/70 px-2.25 text-[11.5px]"
            data-testid="hero-package-meta"
          >
            ID
            <code class="font-mono text-[11px] text-[#007a52]">{{ p.id }}</code>
            <ng-container
              *ngTemplateOutlet="
                copyButton;
                context: {
                  $implicit: p.id,
                  label: 'Copy package ID ',
                  testId: 'hero-copy-package-id'
                }
              "
            />
          </span>
          }
        </div>
        } @if (entry() && pkg(); as p) {
        <div
          class="flex flex-wrap items-center gap-x-2.5 gap-y-2 text-[12.5px] text-[#555]"
          data-testid="hero-package-context"
        >
          <span>in package</span>
          <button
            type="button"
            class="cursor-pointer font-semibold text-[#1b1c19] underline-offset-3 hover:underline"
            (click)="openPackageSettings.emit()"
            data-testid="hero-package-link"
          >
            {{ p.name }}
          </button>
          <span
            class="inline-flex h-6 items-center gap-1.5 rounded-full border border-white bg-white/70 px-2.25"
          >
            <code class="font-mono text-[11.5px] text-[#2a2a2a]"
              >{{ p.id }}</code
            >
            <ng-container
              *ngTemplateOutlet="
                copyButton;
                context: {
                  $implicit: p.id,
                  label: 'Copy package ID ',
                  testId: 'hero-copy-package-id'
                }
              "
            />
          </span>
          @if (p.status; as status) {
          <span
            class="inline-flex items-center gap-1.5 leading-5"
            [enrTooltip]="packageStatusHint[status.tone]"
            enrTooltipPosition="bottom"
            data-testid="hero-package-status"
          >
            @if (status.tone === "sellable") {
            <svg
              class="size-3.5 shrink-0"
              [class]="packageToneClass[status.tone].text"
              viewBox="0 0 16 16"
              fill="none"
              aria-hidden="true"
            >
              <circle cx="8" cy="8" r="7" fill="currentColor" />
              <path
                d="M5 8.2l2 2 4-4.4"
                stroke="#fff"
                stroke-width="1.6"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            } @else {
            <span
              class="inline-flex size-2 shrink-0 rounded-full"
              [class]="packageToneClass[status.tone].dot"
              aria-hidden="true"
            ></span>
            }
            <span
              class="font-semibold whitespace-nowrap"
              [class]="packageToneClass[status.tone].text"
              >{{ status.label }}</span
            >
            @if (status.tone === "sellable") {
            <span
              class="font-medium whitespace-nowrap"
              [class]="packageToneClass[status.tone].text"
              >· sellable</span
            >
            }
          </span>
          } @if (entry(); as e) {
          <span class="text-[#c2c6bd]" aria-hidden="true">|</span>
          <app-price-switcher
            [entries]="entries()"
            [activeId]="e.id"
            [busy]="switching()"
            (priceSelect)="priceSelect.emit($event)"
          />
          }
        </div>
        }
      </div>

      <aside
        class="hero__readiness flex items-center gap-3 rounded-2xl border border-white bg-white/60 px-3.5 py-2.5 backdrop-blur-md"
        aria-label="Publish readiness"
      >
        <div class="relative grid size-14 shrink-0 place-items-center">
          <svg
            [attr.width]="donutSize"
            [attr.height]="donutSize"
            [attr.viewBox]="'0 0 ' + donutSize + ' ' + donutSize"
            aria-hidden="true"
          >
            <circle
              class="hero__readiness-track"
              [attr.cx]="donutSize / 2"
              [attr.cy]="donutSize / 2"
              [attr.r]="donutRadius"
              fill="none"
              [attr.stroke-width]="donutStrokeWidth"
            />
            <circle
              class="hero__readiness-progress"
              [attr.cx]="donutSize / 2"
              [attr.cy]="donutSize / 2"
              [attr.r]="donutRadius"
              fill="none"
              [attr.stroke-width]="donutStrokeWidth"
              stroke-linecap="round"
              [attr.stroke-dasharray]="donutCircumference()"
              [attr.stroke-dashoffset]="donutOffset()"
              [attr.transform]="
                'rotate(-90 ' + donutSize / 2 + ' ' + donutSize / 2 + ')'
              "
            />
          </svg>
          <span
            class="absolute inline-flex items-baseline text-base leading-none font-extrabold tracking-[-0.02em] text-[#007a52] tabular-nums"
            >{{ readinessPercent }}<span
              class="ml-px text-[0.55em] font-bold opacity-75"
              >%</span
            ></span
          >
        </div>
        <div class="min-w-0">
          <div class="text-[12.5px] font-bold">Readiness</div>
          <div class="mt-0.5 text-[11.5px] text-[#555]">
            <b class="text-[#1b1c19]">{{ readinessComplete }}</b> of
            <b class="text-[#1b1c19]">{{ readinessTotal }}</b> sections complete
          </div>
          @if (readinessLabel) {
          <div
            class="mt-0.75 flex items-center gap-1.5 text-xs font-semibold"
            [class]="readinessToneClass[readinessTone()]"
            [attr.data-tone]="readinessTone()"
            data-testid="hero-readiness-label"
          >
            <span
              class="size-1.5 shrink-0 rounded-full bg-current"
              aria-hidden="true"
            ></span>
            {{ readinessLabel }}
          </div>
          }
        </div>
      </aside>
    </div>

    <div
      class="flex items-center gap-2.5 rounded-2xl border border-white bg-white/60 py-2 pr-2.5 pl-3.5 backdrop-blur-md max-[900px]:flex-wrap"
      aria-label="Authoring languages"
    >
      <span class="mr-1 flex-none text-[12.5px] font-bold">Languages</span>
      <div
        class="hero__lang-grid flex min-w-0 flex-1 gap-2.5 max-[900px]:flex-wrap"
        [class.hero__lang-grid--filtered]="hasLanguageFilter()"
      >
        @for (l of activeLanguages(); track l.locale) { @let c = cov(l.locale);
        <div
          class="hero__lang-card flex min-w-0 flex-1 cursor-pointer flex-col gap-1.5 rounded-[10px] border border-white bg-white/75 px-2.5 pt-1.5 pb-1.75 select-none hover:bg-white max-[900px]:min-w-36"
          role="button"
          tabindex="0"
          [attr.aria-pressed]="isLanguageSelected(l)"
          [attr.aria-label]="
            (isLanguageSelected(l) ? 'Showing only ' : 'Show only ') + l.label
          "
          [enrTooltip]="'Show only ' + l.label"
          enrTooltipPosition="bottom"
          [class.hero__lang-card--default]="l.isDefault"
          [class.hero__lang-card--complete]="c.total > 0 && c.filled === c.total"
          [class.hero__lang-card--partial]="c.filled > 0 && c.filled < c.total"
          [class.hero__lang-card--empty]="c.total === 0 || c.filled === 0"
          [class.hero__lang-card--selected]="isLanguageSelected(l)"
          (click)="onLangCardClick(l)"
          (keydown.enter)="onLangCardClick(l); $event.preventDefault()"
          (keydown.space)="onLangCardClick(l); $event.preventDefault()"
        >
          <div class="flex items-center gap-1.75 text-xs">
            <span
              class="hero__lang-card-code inline-grid h-5 min-w-6.5 place-items-center rounded-[5px] bg-[rgba(0,192,122,0.1)] px-1.5 text-[10.5px] font-bold tracking-[0.04em] text-[#007a52]"
              >{{ l.shortCode }}</span
            >
            <span class="min-w-0 flex-1 truncate font-semibold"
              >{{ l.label }}</span
            >
            <span class="font-bold tabular-nums">{{ c.percent }}%</span>
          </div>
          <div
            class="h-1 overflow-hidden rounded-full bg-black/8"
            aria-hidden="true"
          >
            <div
              class="hero__lang-card-bar-fill h-full rounded-full"
              [style.width.%]="c.percent"
            ></div>
          </div>
          <div
            class="flex items-center justify-between gap-1.5 text-[11px] text-[#555]"
          >
            @if (c.total === 0) {
            <span>No localized fields yet</span>
            } @else if (c.filled === c.total) {
            <span
              class="inline-flex items-center gap-1 font-semibold text-[#15803d]"
            >
              <svg
                class="size-2.75 flex-none"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="3"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
              All filled
            </span>
            } @else {
            <span>{{ c.filled }} / {{ c.total }} fields</span>
            @if (c.missing.length > 0) {
            <button
              type="button"
              class="flex-none cursor-pointer text-[11px] font-semibold whitespace-nowrap text-[#007a52] hover:underline"
              cdkOverlayOrigin
              #missingTrigger="cdkOverlayOrigin"
              [attr.aria-expanded]="isMissingOpen(l.locale)"
              (click)="$event.stopPropagation(); toggleMissing(l.locale)"
            >
              {{ isMissingOpen(l.locale) ? "Hide" : "Show" }} {{
              c.missing.length }} unfilled
            </button>
            <ng-template
              cdkConnectedOverlay
              [cdkConnectedOverlayOrigin]="missingTrigger"
              [cdkConnectedOverlayOpen]="isMissingOpen(l.locale)"
              [cdkConnectedOverlayPositions]="missingPopoverPositions"
              [cdkConnectedOverlayPush]="true"
              [cdkConnectedOverlayHasBackdrop]="true"
              cdkConnectedOverlayBackdropClass="cdk-overlay-transparent-backdrop"
              (backdropClick)="closeMissing()"
            >
              <div class="hero__missing-pop">
                <div class="hero__missing-pop-head">
                  {{ c.missing.length }} unfilled · {{ l.label }}
                </div>
                <ul class="hero__missing-pop-list">
                  @for (m of c.missing; track $index) {
                  <li>
                    <button
                      type="button"
                      class="hero__missing-pop-item"
                      [title]="m.label"
                      (click)="onMissingClick(m)"
                    >
                      {{ m.label }}
                    </button>
                  </li>
                  }
                </ul>
              </div>
            </ng-template>
            } }
          </div>
        </div>
        }
      </div>
    </div>
  </div>
</section>

<!-- A package with no price to edit (none at all, or a refused one) is named
     as the package, so the Package settings tab has a truthful header. -->
<ng-template #eyebrow>
  <span
    class="hero__eyebrow inline-flex items-center gap-1.5 text-[11.5px] font-semibold tracking-[0.08em] text-[#007a52] uppercase"
    data-testid="hero-eyebrow"
  >
    @if (entry()) {
    <mat-icon aria-hidden="true">sell</mat-icon> Price } @else {
    <mat-icon aria-hidden="true">inventory_2</mat-icon> Package }
  </span>
</ng-template>

<ng-template #copyButton let-id let-label="label" let-testId="testId">
  <button
    type="button"
    class="cursor-pointer rounded-full bg-[rgba(0,192,122,0.12)] px-1.75 py-0.5 text-[10.5px] font-bold tracking-[0.02em] text-[#007a52] transition-colors hover:bg-[rgba(0,192,122,0.2)]"
    (click)="copy(id)"
    [attr.aria-label]="label + id"
    [attr.data-testid]="testId"
  >
    @if (copied() === id) { ✓ Copied } @else { Copy }
  </button>
</ng-template>
